export interface WatchUser {
    id: string
    displayName: string
    username?: string
    avatar?: string
}

export interface WatchUserDirectoryPage {
    users: WatchUser[]
    recent: WatchUser[]
    page: number
    hasMore: boolean
}

export interface WatchMedia {
    sourceId: string
    id: string
    title: string
    sourceName: string
    poster?: string
    episodeIndex: number
    episodeLabel: string
}

export interface WatchPlaybackState {
    episodeIndex: number
    episodeLabel: string
    position: number
    paused: boolean
    playbackRate: number
    version: number
    updatedAt: number
    temporaryRate?: {
        holdId: string
        basePlaybackRate: number
        expiresAt: number
    }
}

export interface WatchParticipant extends WatchUser {
    role: 'host' | 'viewer'
    connected: boolean
    playbackStatus?: WatchParticipantStatus
}

export type WatchParticipantStatus = 'loading' | 'ready' | 'buffering' | 'error'

export interface WatchSessionSnapshot {
    id: string
    hostId: string
    media: WatchMedia
    playback: WatchPlaybackState
    participants: WatchParticipant[]
    invited: WatchUser[]
    createdAt: number
    expiresAt: number
}

export interface WatchControllerState {
    epoch: number
    isController: boolean
}

export interface WatchInvite {
    sessionId: string
    host: WatchUser
    media: WatchMedia
    expiresAt: number
}

export type WatchPlaybackCommand
    = | { type: 'play', position: number }
        | { type: 'pause', position: number }
        | { type: 'seek', position: number }
        | { type: 'episode', episodeIndex: number, episodeLabel: string, paused: boolean }
        | { type: 'rate', playbackRate: number, position: number }
        | { type: 'rate-hold-start', holdId: string, playbackRate: number, position: number }
        | { type: 'rate-hold-renew', holdId: string }
        | { type: 'rate-hold-end', holdId: string, position: number }
        | { type: 'sync', position: number, paused: boolean, playbackRate: number, episodeIndex: number, episodeLabel: string }

export interface WatchCommandMetadata {
    commandId: string
    controllerEpoch: number
}

export interface WatchGestureFeedback {
    gestureId: string
    kind: 'seek'
    phase: 'update' | 'end' | 'cancel'
    sequence: number
    deltaSeconds: number
    targetPosition: number
    direction: -1 | 1
}

export interface WatchGestureCommandInput {
    type: 'gesture'
    feedback: WatchGestureFeedback
}

export type WatchCommand
    = | ((WatchPlaybackCommand | WatchGestureCommandInput) & WatchCommandMetadata)
        | { type: 'presence', status: WatchParticipantStatus, episodeIndex: number }
        | { type: 'claim-control', commandId: string }
        | { type: 'ping', id: string, clientSentAt: number }

export type WatchCommandInput
    = | WatchPlaybackCommand
        | WatchGestureCommandInput
        | Extract<WatchCommand, { type: 'presence' | 'claim-control' | 'ping' }>

export type WatchSessionMessage
    = | { type: 'snapshot', session: WatchSessionSnapshot, snapshotId: string, serverSentAt: number, controller: WatchControllerState }
        | { type: 'state', state: WatchPlaybackState, serverSentAt: number }
        | { type: 'rate-hold-renewed', holdId: string, playbackVersion: number, expiresAt: number, serverSentAt: number }
        | { type: 'gesture', feedback: WatchGestureFeedback, serverSentAt: number }
        | { type: 'control', controller: WatchControllerState }
        | { type: 'pong', id: string, clientSentAt: number, serverSentAt: number }
        | { type: 'participants', participants: WatchParticipant[], invited: WatchUser[] }
        | { type: 'ended', reason: string }
        | { type: 'kicked' }
        | { type: 'error', message: string }

export type WatchChannelMessage
    = | { type: 'invitations', invitations: WatchInvite[] }
        | { type: 'invite', invitation: WatchInvite }
        | { type: 'invite-removed', sessionId: string }
        | { type: 'session-removed', sessionId: string, eventId: string, reason: string }

export interface WatchChannelCommand { type: 'ack', eventId: string }

export interface WatchUsageStatus {
    day: string
    activeSessions: number
    createdSessions: number
    events: number
    enabled: boolean
    warningLevel: 0 | 70 | 90 | 100
    limits: {
        activeSessions: number
        createdSessions: number
        events: number
    }
}

export const WATCH_PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2] as const
const WATCH_PLAYBACK_RATE_SET = new Set<number>(WATCH_PLAYBACK_RATES)

export function isWatchPlaybackRate(value: number): boolean {
    return WATCH_PLAYBACK_RATE_SET.has(value)
}

export function isWatchParticipantStatus(value: unknown): value is WatchParticipantStatus {
    return value === 'loading' || value === 'ready' || value === 'buffering' || value === 'error'
}

export function projectedWatchPosition(state: WatchPlaybackState, now = Date.now()): number {
    if (state.paused) { return state.position }
    return Math.max(0, state.position + Math.max(0, now - state.updatedAt) / 1000 * state.playbackRate)
}

export interface WatchSyncPlan {
    mode: 'none' | 'rate' | 'seek'
    targetPosition: number
    playbackRate: number
    drift: number
}

const MIN_SYNC_PLAYBACK_RATE = 0.42
const MAX_SYNC_PLAYBACK_RATE = 2.08

export function planWatchSync(state: WatchPlaybackState, localPosition: number, now = Date.now(), force = false): WatchSyncPlan {
    const targetPosition = projectedWatchPosition(state, now)
    const drift = targetPosition - Math.max(0, localPosition)
    const distance = Math.abs(drift)
    if (force || state.paused || distance > 1.2) {
        return { mode: distance < 0.08 && !force ? 'none' : 'seek', targetPosition, playbackRate: state.playbackRate, drift }
    }
    if (distance < 0.25) { return { mode: 'none', targetPosition, playbackRate: state.playbackRate, drift } }
    const adjustmentMagnitude = distance < 0.6 ? 0.04 : distance < 0.9 ? 0.06 : 0.08
    const adjustment = drift > 0 ? adjustmentMagnitude : -adjustmentMagnitude
    // Keep a small correction margin beyond the user-facing rate range so 0.5x/2x
    // sessions can converge without turning every modest drift into a hard seek.
    const playbackRate = Math.max(MIN_SYNC_PLAYBACK_RATE, Math.min(MAX_SYNC_PLAYBACK_RATE, state.playbackRate + adjustment))
    if (playbackRate === state.playbackRate) {
        return { mode: 'seek', targetPosition, playbackRate: state.playbackRate, drift }
    }
    return {
        mode: 'rate',
        targetPosition,
        playbackRate,
        drift,
    }
}
